/**
 * Rollo Phase-1 label helper (pure). Turns a batch's manifest (DTF transfers) + screen-print work into
 * one printable QR label per PHYSICAL garment.
 *
 * Identity is driven through the shared `@fsp/contracts` module — `garmentsForLine` produces the owed
 * garments + their ranks, `encodeGarmentCode` produces the QR payload — so the label's `owedRank` is the
 * exact identity the future Phase-3 scan→settle wiring will resolve (they can't drift). Design names +
 * methods come from `runSheetGarments` (the tested DTF+SP join); the real size comes from the blank-name
 * join (the manifest only carries a coarse adult/youth band). Display-only: no IO, no money path.
 */

import type { PressMethod, SizeBand } from '@fsp/contracts';
import { encodeGarmentCode, garmentsForLine, type PairingLine } from '@fsp/contracts';
import {
  type BatchManifestTransfer,
  runSheetGarments,
  type ScreenPrintWorkItem,
} from './batch-lib';
import { bareBlankSku } from './catalog-lib';
import { type BlankName, normalizeSizeName, pullBlankLabel, sizeRank } from './inventory-lib';

/** A print location on the label (front/back/…) with its design + technique. */
export interface LabelPlacement {
  location: string;
  method: PressMethod;
  designName: string;
  /** The design's short SKU-style code (FR/FW/LC/LT-001), live-joined by designId; absent until set. */
  styleCode?: string;
}

/** One printable label = one physical garment. */
export interface GarmentLabel {
  /** QR payload `v1|storeId|orderId|lineId|owedRank`. */
  code: string;
  storeId: string;
  orderId: string;
  lineId: string;
  orderName: string;
  /** 1-based owed rank within the line (the QR's owedRank). */
  rank: number;
  /** Owed garments for this line — the "N" in "copy R of N". */
  copyOf: number;
  /** The garment's one glanceable badge: the FRONT-FAMILY placement's style code when set, else the
   *  legacy first-two-letters of its design name; `—` only for a back-only garment. See
   *  `frontFamilyPlacement` — a left-chest-only garment is named by its LC code, not a dash. The label
   *  itself draws a badge box per front-family placement; this is the single-badge form. */
  primaryBadge: string;
  front?: LabelPlacement;
  back?: LabelPlacement;
  /** Any non-front/back placements (left-chest, sleeve, …). */
  extras: LabelPlacement[];
  /** Blank body, e.g. "Tultex 602" (falls back to the Shopify title for a nameless blank). */
  bodyLabel: string;
  /** Blank colour, e.g. "Black" (empty if unknown). */
  colorName: string;
  /** Real size, e.g. "L" — from the resolved blank, falling back to the variant title. */
  sizeName: string;
  /** Coarse print band, classified upstream at catalog sync from the SHOPIFY variant size and carried
   *  through the batch item to `RunSheetGarment.sizeBand`. Drives the YOUTH marker. Adult is the safe
   *  default: youth is applied only on a positive match, never inferred from a size list here. */
  sizeBand: SizeBand;
}

/** An on-screen grouping of labels by blank body/colour/size (for pull-by-size-stack counts). */
export interface LabelGroup {
  key: string;
  bodyLabel: string;
  colorName: string;
  sizeName: string;
  count: number;
}

/** First two letters of a design name, uppercased; `—` when fewer than two letters are available. */
export function frontBadge(designName: string | undefined): string {
  const letters = (designName ?? '').replace(/[^A-Za-z]/g, '').toUpperCase();
  return letters.length >= 2 ? letters.slice(0, 2) : '—';
}

/**
 * The boxed-badge label for a front-family placement: its style code when set, else the legacy
 * first-two-letters of the design name (so a design without a code yet still reads like today's label).
 * `—` when there is no placement / neither is available.
 */
export function placementBadge(
  p: Pick<LabelPlacement, 'styleCode' | 'designName'> | undefined,
): string {
  return p?.styleCode ?? frontBadge(p?.designName);
}

/**
 * The one placement that NAMES a garment at a glance: `front` when it has one, else the first print
 * that isn't the back (left-chest, sleeve, …). "Front family" = everything but the back — the prints an
 * operator sees while the shirt faces them.
 *
 * Every surface that shows a SINGLE badge derives it through here, so they can't disagree about which
 * print names the garment. Looking only at `location === 'front'` is the trap: a left-chest-only garment
 * has no `front` placement, so the /scan band showed a bare `—` while its printed label read `LC`.
 * Returns undefined for a back-only garment (nothing on the front to name it by).
 */
export function frontFamilyPlacement<T extends { location: string }>(
  placements: readonly T[],
): T | undefined {
  return (
    placements.find((p) => p.location === 'front') ?? placements.find((p) => p.location !== 'back')
  );
}

function toLabelPlacement(
  p: LabelSourcePlacement,
  styleCodeByDesignId: ReadonlyMap<string, string>,
): LabelPlacement {
  const styleCode = p.designId ? styleCodeByDesignId.get(p.designId) : undefined;
  return {
    location: p.location,
    method: p.method,
    designName: p.designName ?? '',
    ...(styleCode ? { styleCode } : {}),
  };
}

/** The minimum a placement must carry for {@link labelForGarment}. Structural rather than a concrete
 *  type, so both a manifest-derived `RunSheetPlacement` and a `/scan/resolve` placement satisfy it
 *  without either side importing the other's shape. */
export interface LabelSourcePlacement {
  location: string;
  method: PressMethod;
  designId?: string | undefined;
  designName?: string | undefined;
}

/** Everything needed to render ONE garment label. See {@link labelForGarment} for why it is explicit. */
export interface LabelGarmentSource {
  /** The QR payload. The batch sheet computes it with `encodeGarmentCode`; a reprint passes the SCANNED
   *  string back verbatim, so the reissued tag is byte-identical to the one it replaces. */
  code: string;
  storeId: string;
  orderId: string;
  lineId: string;
  orderName: string;
  rank: number;
  copyOf: number;
  placements: readonly LabelSourcePlacement[];
  /** `BLANK#<ssSku>` frozen on the batch item — NOT the live order line's, which is re-derived from the
   *  catalog on every webhook and reconcile. Absent for an unmapped line. */
  resolvedBlankSku?: string | undefined;
  /** Shopify product title, the fallback when no blank resolves. */
  title?: string | undefined;
  /** Shopify variant title, the fallback when no blank resolves (its size half is recovered by
   *  `normalizeSizeName`). */
  variantTitle?: string | undefined;
  sizeBand: SizeBand;
}

/**
 * Build ONE garment label. The single path by which a `GarmentLabel` is produced — `garmentLabels`
 * calls it per owed copy, and the `/scan` hem-tag reprint calls it once.
 *
 * ⚠️ THE WHOLE POINT is that a reprint cannot silently differ from the original, and that is a property
 * of these ARGUMENTS, not of this function (`docs/label-mislabel-mitigation-plan-v1.md` § 8.3). Sharing
 * the function guarantees the same *derivation*; it guarantees nothing about the inputs. Specifically:
 *
 *  · `nameBySku` cold (the `['inventory']` query still pending) yields an empty map, so `bodyLabel`
 *    falls back to the raw Shopify title and `colorName` empties — a tag that reads differently from the
 *    one it replaces. An empty map is INDISTINGUISHABLE from a resolved-but-empty catalogue here, so the
 *    caller must gate on the query having RESOLVED, not merely been requested.
 *  · `styleCodeByDesignId` cold degrades every badge to the legacy first-two-letters form.
 *  · `placements` must include screen-print as well as DTF. (`/scan/resolve` already merges both, so its
 *    caller gets this for free; a manifest-only caller must pass `screenPrintWork` too.)
 *
 * Callers that cannot guarantee resolved inputs must not render a label at all.
 */
export function labelForGarment(
  g: LabelGarmentSource,
  nameBySku: ReadonlyMap<string, BlankName>,
  styleCodeByDesignId: ReadonlyMap<string, string> = new Map(),
): GarmentLabel {
  const name = g.resolvedBlankSku ? nameBySku.get(bareBlankSku(g.resolvedBlankSku)) : undefined;
  const joined = g.placements.map((p) => toLabelPlacement(p, styleCodeByDesignId));
  return {
    code: g.code,
    storeId: g.storeId,
    orderId: g.orderId,
    lineId: g.lineId,
    orderName: g.orderName,
    rank: g.rank,
    copyOf: g.copyOf,
    // The label renderer draws a badge box per front-family placement; this is the single-badge form for
    // callers that show exactly one (the /scan identity band).
    primaryBadge: placementBadge(frontFamilyPlacement(joined)),
    front: joined.find((p) => p.location === 'front'),
    back: joined.find((p) => p.location === 'back'),
    extras: joined.filter((p) => p.location !== 'front' && p.location !== 'back'),
    bodyLabel: pullBlankLabel(name, g.title ?? ''),
    colorName: name?.colorName ?? '',
    // Normalize so an unresolved blank (size falls back to the Shopify variant title, e.g. "Black / L")
    // both DISPLAYS as a clean size ("L") and SORTS in size order — not sunk to the end as a raw string.
    sizeName: normalizeSizeName(name?.sizeName ?? g.variantTitle ?? ''),
    sizeBand: g.sizeBand,
  };
}

/**
 * Why QR labels must not be printed from THIS manifest, or `null` when they may be (L0,
 * `docs/label-mislabel-mitigation-plan-v1.md` § 8.4).
 *
 * The single owner of the rule, so no call site can decide it independently — the same reason
 * `reprintPlacementsFor` exists in `@fsp/contracts`. Returns operator-facing COPY rather than a boolean,
 * because a surface that renders nothing without saying why is how staff conclude the app is broken and go
 * back to counting pages.
 *
 * ⚠️ A reprint sheet re-ranks its copies from 1, and the only `copyIndex` → SOURCE-rank map
 * (`BatchItemRecord.reissueRanks`) is on the batch ITEM, not on the manifest. `garmentLabels` therefore
 * encodes `owedRank 1` for a garment that is really source rank 3, and that QR scans as a DIFFERENT
 * physical shirt: the press checks off the wrong garment and the real one ships unpressed. A WRONG tag is
 * strictly worse than no tag, so this refuses rather than degrades.
 *
 * Lifting it is a real feature, not a flag flip: thread `reissueRanks` onto the manifest transfer and have
 * `garmentLabels` encode the source rank. Until then a replacement blank is tagged from its SOURCE sheet.
 */
export function labelPrintBlockReason(manifest: { reissueOf?: string | undefined }): string | null {
  if (manifest.reissueOf) {
    return 'This is a reprint sheet, and its copy numbers restart at 1 — so a label printed here would carry the wrong garment’s code and the press would check off the wrong shirt. Print the tag from the ORIGINAL sheet instead; a kept blank already wears its label.';
  }
  return null;
}

/**
 * One QR label per owed physical garment across the batch, sorted by size → blank body → colour → order →
 * rank so labels peel off in size-stack order (all S, then all M, …). Skips cancelled lines and refunded
 * (do-not-press) copies.
 *
 * ⚠️ Callers MUST consult {@link labelPrintBlockReason} first and render its reason instead of calling
 * this. The ranks it encodes are only authoritative for a first-run sheet.
 */
export function garmentLabels(
  manifest: { transfers: readonly BatchManifestTransfer[] },
  screenPrintWork: readonly ScreenPrintWorkItem[],
  nameBySku: ReadonlyMap<string, BlankName>,
  // designId → styleCode, live-joined at render (see useStyleCodes). Empty by default so callers/tests
  // that don't care about codes are unaffected; a missing code falls back to the name-derived badge.
  styleCodeByDesignId: ReadonlyMap<string, string> = new Map(),
): GarmentLabel[] {
  // runSheetGarments keys garments by `store#order#line` but doesn't surface lineId as a field — recover
  // it from the source rows (needed for the QR payload). Both techniques key identically.
  const lineIdByKey = new Map<string, string>();
  for (const t of manifest.transfers) {
    lineIdByKey.set(`${t.storeId}#${t.orderId}#${t.lineId}`, t.lineId);
  }
  for (const sp of screenPrintWork) {
    lineIdByKey.set(`${sp.storeId}#${sp.orderId}#${sp.lineId}`, sp.lineId);
  }

  const labels: GarmentLabel[] = [];
  for (const g of runSheetGarments(manifest, screenPrintWork)) {
    const owedCount = g.lineCancelled ? 0 : Math.max(0, g.quantity - g.qtyDropped);
    if (owedCount === 0) continue;
    const lineId = lineIdByKey.get(g.key);
    if (!lineId) continue; // defensive: every run-sheet garment traces to a source row

    // Route the physical-garment set + ranks through the shared pairing helper so the label's owedRank is
    // identical to what the Phase-3 settle-wire will resolve. batchedQty = frozen copies; currentQty = owed
    // (trailing-trimmed), so garmentsForLine flags exactly ranks 1..owedCount owed.
    const pairing: PairingLine = {
      batchedQty: g.quantity,
      currentQty: owedCount,
      cancelled: g.lineCancelled,
      placements: g.placements.map((p) => ({ location: p.location, method: p.method })),
    };

    for (const gm of garmentsForLine(pairing)) {
      if (!gm.owed) continue; // do-not-press copies get no label
      labels.push(
        labelForGarment(
          {
            code: encodeGarmentCode({
              storeId: g.storeId,
              orderId: g.orderId,
              lineId,
              owedRank: gm.rank,
            }),
            storeId: g.storeId,
            orderId: g.orderId,
            lineId,
            orderName: g.orderName,
            rank: gm.rank,
            copyOf: owedCount,
            placements: g.placements,
            resolvedBlankSku: g.resolvedBlankSku,
            title: g.title,
            variantTitle: g.variantTitle,
            // `RunSheetGarment.sizeBand` is widened to `string` on the way through batch-lib, so narrow
            // it here rather than casting — and default to adult, so a band we don't recognise never
            // claims a garment is youth.
            sizeBand: g.sizeBand === 'youth' ? 'youth' : 'adult',
          },
          nameBySku,
          styleCodeByDesignId,
        ),
      );
    }
  }

  // Size FIRST: staff pull garments into piles by size, so labels peel off as one contiguous run per
  // size (all S, then all M, …) — even across mixed blank bodies/colours in the batch. Within a size,
  // group by body → colour → order so identical garments stay adjacent, then by owed rank.
  labels.sort(
    (a, b) =>
      sizeRank(a.sizeName) - sizeRank(b.sizeName) ||
      a.sizeName.localeCompare(b.sizeName) ||
      a.bodyLabel.localeCompare(b.bodyLabel) ||
      a.colorName.localeCompare(b.colorName) ||
      a.orderName.localeCompare(b.orderName) ||
      a.rank - b.rank,
  );
  return labels;
}

/** The pile identity of a label: blank body/colour/size. The on-screen group summary and the printed
 *  pile divider both key off THIS, so a divider can never name a group the summary doesn't recognise. */
function labelGroupKey(l: GarmentLabel): string {
  return `${l.bodyLabel}##${l.colorName}##${l.sizeName}`;
}

/** Group pre-sorted labels by blank body/colour/size, preserving sort order, with per-group counts. */
export function groupLabels(labels: readonly GarmentLabel[]): LabelGroup[] {
  const groups = new Map<string, LabelGroup>();
  for (const l of labels) {
    const key = labelGroupKey(l);
    const existing = groups.get(key);
    if (existing) existing.count += 1;
    else
      groups.set(key, {
        key,
        bodyLabel: l.bodyLabel,
        colorName: l.colorName,
        sizeName: l.sizeName,
        count: 1,
      });
  }
  return [...groups.values()];
}

/** One pile divider in the label strip.
 *
 *  Carries NO `code` — a divider is deliberately unscannable. A scannable divider would resolve to
 *  nothing at the /scan kiosk, or worse be taken for a garment.
 */
export interface PileDivider {
  /** The {@link groupLabels} key, `bodyLabel##colorName##sizeName`. NOT unique across the strip — one
   *  group can yield several runs (see {@link labelStrip}) — so a renderer must key off `index` too;
   *  `label-sheet.tsx` uses `pile:${index}:${key}`. A divider has no QR payload to key off. */
  key: string;
  /** Blank body, e.g. "Tultex 602" (falls back to the Shopify title for a nameless blank). */
  bodyLabel: string;
  /** Blank colour, e.g. "Black" — empty when unknown. */
  colorName: string;
  /** Real size, e.g. "L", already through `normalizeSizeName`. */
  sizeName: string;
  /** Labels in the run this tag introduces — how many to peel off behind it. */
  count: number;
  /** 1-based position among the batch's piles, and the total → "PILE 3 OF 6". */
  index: number;
  total: number;
  /** Coarse print band of the run, so the pile tag itself carries the YOUTH marker. The band is part of
   *  the RUN identity (not just read off the first label), because the group key is built from DISPLAY
   *  strings — `bodyLabel` falls back to the free-text Shopify title and `colorName` can be empty, so an
   *  adult and a youth garment CAN collide on one key and would otherwise share one tag reading "adult",
   *  hiding the youth marker on exactly the mislabel this feature exists to prevent. */
  sizeBand: SizeBand;
}

/** A printable page in the label strip: a garment label, or the pile divider that introduces its run. */
export type LabelStripEntry =
  | { kind: 'divider'; divider: PileDivider }
  | { kind: 'label'; label: GarmentLabel };

/**
 * Interleave a pile divider before each (body, colour, size) run of a SORTED label list.
 *
 * WHY this exists: the strip prints as one uninterrupted reel, and an operator peeling in order crosses
 * from a tee pile to a hoodie pile with nothing physical to stop them — the reported mislabel (labels for
 * hoodies and for youth styles applied to a pile of adult tees, caught only after the heat press, i.e.
 * after both the blank and the transfer are spent). A divider is a real 51×25 mm page, so it INTERRUPTS
 * the peel run, and it doubles as the tag staff stick on the pile or box as they pick it.
 *
 * One divider per CONTIGUOUS run, not one per distinct key with a seen-set. `count` and `index` are
 * promises about the labels that physically follow this tag, and contiguity is a `localeCompare` property
 * of `garmentLabels`' sort rather than a guarantee: NFC vs NFD, a soft hyphen and a ZWSP all collate
 * equal, and the `##` key separator is unescaped — all reachable through `bodyLabel`, which falls back to
 * the free-text Shopify product title. A seen-set builder would print a tag whose count is a lie about
 * how many labels to peel, which is the exact error this feature exists to prevent. On well-formed input
 * the run count equals `groupLabels(labels).length` (asserted in the tests).
 */
export function labelStrip(labels: readonly GarmentLabel[]): LabelStripEntry[] {
  const runs: { key: string; first: GarmentLabel; count: number }[] = [];
  for (const label of labels) {
    const key = labelGroupKey(label);
    const open = runs.at(-1);
    // Break the run when the BAND changes even if the group key matches. The key is display strings, so
    // an adult and a youth garment collide whenever `bodyLabel` falls back to the Shopify title (colour
    // and size then come from the same free-text variant) — one shared tag would read "adult" over youth
    // garments, which is the precise mislabel this tag exists to stop. Splitting costs one extra tag.
    if (open && open.key === key && open.first.sizeBand === label.sizeBand) open.count += 1;
    else runs.push({ key, first: label, count: 1 });
  }

  const strip: LabelStripEntry[] = [];
  let at = 0;
  for (const [i, run] of runs.entries()) {
    strip.push({
      kind: 'divider',
      divider: {
        key: run.key,
        bodyLabel: run.first.bodyLabel,
        colorName: run.first.colorName,
        sizeName: run.first.sizeName,
        count: run.count,
        index: i + 1,
        total: runs.length,
        sizeBand: run.first.sizeBand,
      },
    });
    for (const label of labels.slice(at, at + run.count)) strip.push({ kind: 'label', label });
    at += run.count;
  }
  return strip;
}
